<template>
  <transition name="whatsapp-panel-root">
    <div
      v-if="visible"
      class="ContactWhatsappConversationPanel__root"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('contacts.whatsappConversationTitle')"
    >
      <div
        class="ContactWhatsappConversationPanel__backdrop"
        @click="close"
      />
      <div
        class="ContactWhatsappConversationPanel__panel"
        @click.stop
      >
          <header class="ContactWhatsappConversationPanel__header">
            <div class="ContactWhatsappConversationPanel__header-text">
              <h2 class="ContactWhatsappConversationPanel__title">
                {{ $t('contacts.whatsappConversationTitle') }}
              </h2>
              <div class="ContactWhatsappConversationPanel__header-meta">
                <p v-if="displayLead" class="ContactWhatsappConversationPanel__meta">
                  {{ $t('contacts.whatsappConversationLead') }} : {{ displayLead }}
                </p>
                <p v-if="displayProperty" class="ContactWhatsappConversationPanel__meta">
                  {{ $t('contacts.whatsappConversationProperty') }} : {{ displayProperty }}
                </p>
                <el-tag
                  v-if="detail.qualificationLabel"
                  :type="detail.isQualified ? 'success' : 'info'"
                  size="small"
                  class="ContactWhatsappConversationPanel__badge"
                >
                  {{ detail.qualificationLabel }}
                </el-tag>
              </div>
            </div>
            <button
              type="button"
              class="ContactWhatsappConversationPanel__close"
              :aria-label="$t('contacts.whatsappConversationClose')"
              @click="close"
            >
              <i class="el-icon-close" />
            </button>
          </header>

          <div
            v-loading="loading"
            class="ContactWhatsappConversationPanel__body contact-whatsapp-conversation-body"
            :element-loading-text="$t('contacts.whatsappConversationLoading')"
          >
            <template v-if="!loading && loadAttempted">
              <section
                v-if="detail.qualifiedFields.length"
                class="ContactWhatsappConversationPanel__section ContactWhatsappConversationPanel__section--qualified contact-whatsapp-conversation-qualified"
              >
                <h3 class="ContactWhatsappConversationPanel__section-title">
                  {{ $t('contacts.whatsappConversationQualifiedData') }}
                </h3>
                <ul class="ContactWhatsappConversationPanel__qualified-list">
                  <li
                    v-for="(row, idx) in detail.qualifiedFields"
                    :key="idx"
                    class="ContactWhatsappConversationPanel__qualified-row"
                  >
                    <span class="ContactWhatsappConversationPanel__qualified-label">{{ row.label }}</span>
                    <span class="ContactWhatsappConversationPanel__qualified-value">{{ row.value }}</span>
                  </li>
                </ul>
              </section>

              <section class="ContactWhatsappConversationPanel__section ContactWhatsappConversationPanel__section--chat">
                <h3 class="ContactWhatsappConversationPanel__section-title">
                  {{ $t('contacts.whatsappConversationTranscript') }}
                </h3>
                <p
                  v-if="!detail.messages.length"
                  class="ContactWhatsappConversationPanel__empty-chat"
                >
                  {{ $t('contacts.whatsappConversationNoThread') }}
                </p>
                <div
                  v-else
                  class="ContactWhatsappConversationPanel__messages"
                >
                  <div
                    v-for="(msg, idx) in detail.messages"
                    :key="idx"
                    class="ContactWhatsappConversationPanel__msg-row"
                    :class="msg.from === 'agent' ? 'is-agent' : 'is-interlocutor'"
                  >
                    <div class="ContactWhatsappConversationPanel__bubble">
                      <span class="ContactWhatsappConversationPanel__bubble-text">{{ msg.body }}</span>
                    </div>
                    <div
                      v-if="msg.time"
                      class="ContactWhatsappConversationPanel__time"
                    >
                      {{ msg.time }}
                    </div>
                  </div>
                </div>
              </section>
            </template>
          </div>
        </div>
    </div>
  </transition>
</template>

<script>
import { buildDetailFromPrefetchedThread } from '@/pages/contacts/whatsappThreadDisplay'

const emptyDetail = () => ({
  leadLabel: '',
  propertyLabel: '',
  qualificationLabel: '',
  isQualified: false,
  qualifiedFields: [],
  messages: []
})

export default {
  name: 'ContactWhatsappConversationPanel',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    prefetchedThread: {
      type: Object,
      default: null
    },
    fallbackLeadLabel: {
      type: String,
      default: ''
    },
    fallbackPropertyLabel: {
      type: String,
      default: ''
    },
    typeOffre: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      loading: false,
      loadAttempted: false,
      detail: emptyDetail()
    }
  },
  computed: {
    displayLead () {
      return this.detail.leadLabel || this.fallbackLeadLabel || ''
    },
    displayProperty () {
      return this.detail.propertyLabel || this.fallbackPropertyLabel || ''
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler (val) {
        if (val) {
          this.loadDetail()
        } else {
          this.resetState()
        }
      }
    },
    prefetchedThread: {
      deep: true,
      handler () {
        if (this.visible) {
          this.loadDetail()
        }
      }
    }
  },
  methods: {
    close () {
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    resetState () {
      this.loading = false
      this.loadAttempted = false
      this.detail = emptyDetail()
    },
    resolvePanelDateLocale () {
      const lang = this.$i18n && this.$i18n.i18next
        ? this.$i18n.i18next.language
        : 'fr'
      if (lang === 'en') {
        return 'en-GB'
      }
      if (lang === 'mx') {
        return 'es-MX'
      }
      if (lang === 'es') {
        return 'es-ES'
      }
      return 'fr-FR'
    },
    async loadDetail () {
      this.loading = true
      this.loadAttempted = false

      const thread = (this.prefetchedThread && typeof this.prefetchedThread === 'object')
        ? this.prefetchedThread
        : null
      const prefetchedData = buildDetailFromPrefetchedThread({
        thread,
        t: this.$t.bind(this),
        leadLabel: this.fallbackLeadLabel,
        propertyLabel: this.fallbackPropertyLabel,
        locale: this.resolvePanelDateLocale(),
        typeOffre: this.typeOffre
      })
      this.detail = { ...emptyDetail(), ...prefetchedData }
      this.loadAttempted = true
      this.loading = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/theme/_bootstrap.scss";
@import "@/theme/_element.variables.scss";

.ContactWhatsappConversationPanel__root {
  position: fixed;
  inset: 0;
  z-index: 3000;
  pointer-events: none;
}

.ContactWhatsappConversationPanel__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  pointer-events: auto;
}

.ContactWhatsappConversationPanel__panel {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: 640px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
  border-radius: 8px 0 0 0;
  pointer-events: auto;
  overflow: hidden;
}

.ContactWhatsappConversationPanel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid $--border-color-lighter;
  flex-shrink: 0;
}

.ContactWhatsappConversationPanel__header-text {
  min-width: 0;
  padding-right: 8px;
}

.ContactWhatsappConversationPanel__header-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.ContactWhatsappConversationPanel__title {
  margin: 0 0 12px;
  font-size: 1.28rem;
  font-weight: 600;
  line-height: 1.25;
  /* Garde la casse des traductions (ex. « WhatsApp ») si un style global met les titres en capitales */
  text-transform: none !important;
}

.ContactWhatsappConversationPanel__meta {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.45;
  color: $gray-600;
}

.ContactWhatsappConversationPanel__badge {
  margin-top: 2px;
}

.ContactWhatsappConversationPanel__close {
  flex-shrink: 0;
  border: none;
  background: transparent;
  padding: 4px;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  color: $gray-600;

  &:hover {
    color: $--color-text-primary;
  }
}

/* Corps scrollable : fond blanc, distinct du seul encart "données qualifiées" (gris) */
.contact-whatsapp-conversation-body {
  flex: 1;
  min-height: 200px;
  overflow: auto;
  padding: 18px 24px 28px;
  background: #fff;
}

.ContactWhatsappConversationPanel__section-title {
  margin: 0 0 14px;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: 0.01em;
  /* Casse phrase : 1re lettre en majuscule, le reste en minuscule (neutralise un éventuel uppercase global sur h3) */
  text-transform: lowercase !important;
}

.ContactWhatsappConversationPanel__section-title::first-letter {
  text-transform: uppercase !important;
}

/* Fond gris : uniquement « Données qualifiées » — couleur unie, pleine largeur du conteneur scroll (annule le padding du body) */
.contact-whatsapp-conversation-qualified {
  $body-h-pad: 24px;

  background-color: $gray-200;
  margin: 0 (-$body-h-pad) 0;
  padding: 16px $body-h-pad 16px;
  border: none;
  border-bottom: 1px solid $gray-400;
  border-radius: 0;
  box-shadow: none;

  .ContactWhatsappConversationPanel__section-title {
    margin-bottom: 12px;
  }
}

.contact-whatsapp-conversation-qualified
  + .ContactWhatsappConversationPanel__section--chat {
  margin-top: 20px;
}

.ContactWhatsappConversationPanel__qualified-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.ContactWhatsappConversationPanel__qualified-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid $--border-color-lighter;
  font-size: 0.9375rem;

  &:last-child {
    border-bottom: none;
  }
}

.ContactWhatsappConversationPanel__qualified-label {
  color: $gray-600;
}

.ContactWhatsappConversationPanel__qualified-value {
  font-weight: 500;
  text-align: right;
}

.ContactWhatsappConversationPanel__section--chat {
  /* fond blanc : hérité de .contact-whatsapp-conversation-body */
  margin: 0;
  padding: 0;
}

.ContactWhatsappConversationPanel__empty-chat {
  margin: 0 0 8px;
  font-size: 0.9375rem;
  line-height: 1.45;
  color: $gray-600;
}

.ContactWhatsappConversationPanel__section--chat .ContactWhatsappConversationPanel__section-title {
  margin-bottom: 18px;
}

.ContactWhatsappConversationPanel__messages {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ContactWhatsappConversationPanel__msg-row {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  align-items: stretch;
  text-align: left;
}

.ContactWhatsappConversationPanel__bubble {
  display: inline-flex;
  justify-content: flex-start;
  align-items: flex-start;
  width: max-content;
  max-width: 82%;
  height: auto;
  min-height: 0;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 0.9375rem;
  line-height: 1.35;
  text-align: left !important;
  direction: ltr;
  word-break: break-word;
  white-space: pre-wrap;
}

.ContactWhatsappConversationPanel__bubble-text {
  display: block;
  width: 100%;
  margin: 0;
  text-align: left !important;
  direction: ltr;
}

.is-interlocutor .ContactWhatsappConversationPanel__bubble {
  margin-right: auto;
  background: #c3c3c39f;
  color: $--color-text-primary;
}

.is-agent .ContactWhatsappConversationPanel__bubble {
  margin-left: auto;
  background: $--color-primary;
  color: #fff;
}

.ContactWhatsappConversationPanel__time {
  width: max-content;
  margin-top: 8px;
  padding-top: 2px;
  font-size: 0.75rem;
  line-height: 1.25;
  color: $gray-600;
}

.is-interlocutor .ContactWhatsappConversationPanel__time {
  margin-right: auto;
}

.is-agent .ContactWhatsappConversationPanel__time {
  margin-left: auto;
}

.whatsapp-panel-root-enter-active,
.whatsapp-panel-root-leave-active {
  transition: opacity 0.26s ease;

  .ContactWhatsappConversationPanel__panel {
    transition: transform 0.28s ease;
  }
}

.whatsapp-panel-root-enter,
.whatsapp-panel-root-leave-to {
  opacity: 0;

  .ContactWhatsappConversationPanel__panel {
    transform: translateX(100%);
  }
}
</style>
